import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../providers/auth-provider';
import { useTheme } from '../providers/theme-provider';

export function UserMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  const displayName = user?.email ?? 'Signed out';
  const initials = (user?.email ?? '?').slice(0, 1).toUpperCase();
  const roleLabel = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Guest';

  useEffect(() => {
    if (!open) return;
    // Shadow DOM fix: when this component lives inside a shadow root, the
    // event's `target` is retargeted to the custom-element host by the time
    // it bubbles to `document`, so `ref.current.contains(e.target)` is
    // always false — and "click outside" fires for every click, closing
    // the menu before the inner button's onClick can run.
    // `composedPath()` returns the real propagation path *including* the
    // pre-retarget targets, and works the same in light DOM and Shadow DOM.
    const onDoc = (e: MouseEvent) => {
      const node = ref.current;
      if (!node) return;
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (path.includes(node)) return;
      if (!path.length && e.target instanceof Node && node.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleSignOut = () => {
    setOpen(false);
    logout();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={displayName}
        aria-label="User menu"
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: 'var(--bg-alt)',
          border: '1px solid var(--border-strong)',
          color: 'var(--text)',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        {initials}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 30,
            right: 0,
            width: 260,
            background: 'var(--bg)',
            border: '1px solid var(--border-strong)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-soft)',
            zIndex: 60,
            padding: '8px 0',
            fontFamily: 'inherit',
          }}
        >
          <MenuBlock label="Signed in as">
            <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, wordBreak: 'break-all' }}>
              {displayName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{roleLabel}</div>
          </MenuBlock>
          <Divider />
          <div
            style={{
              padding: '6px 14px 2px',
              fontSize: 10,
              color: 'var(--text-mute)',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontWeight: 600,
            }}
          >
            Appearance
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '4px 14px 8px' }}>
            {(['light', 'dark'] as const).map((o) => (
              <button
                key={o}
                onClick={() => setTheme(o)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: 12,
                  background: theme === o ? 'var(--accent-soft)' : 'transparent',
                  color: theme === o ? 'var(--accent)' : 'var(--text-soft)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: theme === o ? 600 : 400,
                  textTransform: 'capitalize',
                }}
              >
                {o}
              </button>
            ))}
          </div>
          <Divider />
          <MenuRow>Docs</MenuRow>
          <MenuRow>Tutorial</MenuRow>
          <MenuRow>Send feedback</MenuRow>
          <Divider />
          <MenuRow onClick={handleSignOut}>Sign out</MenuRow>
        </div>
      )}
    </div>
  );
}

function MenuBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '8px 14px 10px' }}>
      <div
        style={{
          fontSize: 10,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />;
}

function MenuRow({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '8px 14px',
        background: 'transparent',
        border: 0,
        fontSize: 13,
        color: 'var(--text)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = 'var(--accent-softer)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}
